import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../lib/instant";
import { useAuth } from "../context/AuthContext";
import { User, Phone, Mail, MapPin, Plus, Trash2, LogOut, Package, Edit2, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { id as instantId } from "../lib/instant";

export default function Profile({ theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const accentColor = theme?.accentColor || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [editField, setEditField]     = useState(null);
  const [editVal, setEditVal]         = useState("");
  const [addingAddress, setAddingAddress] = useState(false);
  const [editAddrId, setEditAddrId]   = useState(null); // which address is being edited
  const [addrForm, setAddrForm]       = useState({ label: "Home", name: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [savingAddr, setSavingAddr]   = useState(false);

  const { data } = db.useQuery(user ? {
    profiles: { $: { where: { userId: user.id } } },
    addresses: { $: { where: { userId: user.id } } },
    orders: { $: { where: { userId: user.id } } },
  } : {});

  const profile = data?.profiles?.[0] || {};
  const addresses = data?.addresses || [];
  const orders = data?.orders || [];

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
    toast.success("Signed out successfully");
  };

  const saveProfileField = async (field, value) => {
    if (!user) return;
    try {
      const pid = profile.id || instantId();
      await db.transact([db.tx.profiles[pid].update({ userId: user.id, [field]: value, updatedAt: new Date().toISOString() })]);
      toast.success("Updated!");
      setEditField(null);
    } catch { toast.error("Failed to update"); }
  };

  const saveAddress = async () => {
    if (!addrForm.name || !addrForm.address || !addrForm.pincode) { toast.error("Fill Name, Address and PIN Code"); return; }
    setSavingAddr(true);
    try {
      if (editAddrId) {
        // Update existing
        await db.transact([db.tx.addresses[editAddrId].update({ ...addrForm, updatedAt: new Date().toISOString() })]);
        toast.success("Address updated!");
        setEditAddrId(null);
      } else {
        // Add new
        await db.transact([db.tx.addresses[instantId()].update({ ...addrForm, userId: user.id, createdAt: new Date().toISOString() })]);
        toast.success("Address saved!");
      }
      setAddingAddress(false);
      setAddrForm({ label: "Home", name: "", phone: "", address: "", city: "", state: "", pincode: "" });
    } catch { toast.error("Failed to save address"); }
    setSavingAddr(false);
  };

  const openEditAddress = (addr) => {
    setAddrForm({ label: addr.label || "Home", name: addr.name || "", phone: addr.phone || "", address: addr.address || "", city: addr.city || "", state: addr.state || "", pincode: addr.pincode || "" });
    setEditAddrId(addr.id);
    setAddingAddress(true);
  };

  const deleteAddress = async (id) => {
    await db.transact([db.tx.addresses[id].delete()]);
    toast.success("Address removed");
  };

  if (!user) return (
    <div style={{ paddingTop: 100, minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 20 }}>
      <h2 style={{ color: "#2c1810", fontFamily: "Georgia, serif" }}>Please log in</h2>
      <Link to="/login" style={{ background: primaryColor, color: "#fff", padding: "12px 32px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>Login</Link>
    </div>
  );

  const totalSpent = orders.filter(o => o.status !== "Cancelled").reduce((s, o) => s + Number(o.total || 0), 0);

  const inputStyle = { width: "100%", padding: "10px 12px", border: "1.5px solid #e0e0e0", borderRadius: 8, fontSize: "0.9rem", outline: "none", fontFamily: "inherit" };

  const EditableRow = ({ icon, label, field, value, placeholder }) => {
    const isEditing = editField === field;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: "1px solid #f5f5f5" }}>
        <div style={{ color: primaryColor, flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "0.72rem", color: "#aaa", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
          {isEditing ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input value={editVal} onChange={e => setEditVal(e.target.value)} placeholder={placeholder}
                style={{ ...inputStyle, padding: "7px 10px" }} autoFocus />
              <button onClick={() => saveProfileField(field, editVal)} style={{ background: secondaryColor, border: "none", color: "#fff", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Check size={14}/></button>
              <button onClick={() => setEditField(null)} style={{ background: "#f5f5f5", border: "none", color: "#888", width: 30, height: 30, borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={14}/></button>
            </div>
          ) : (
            <div style={{ fontWeight: 600, color: value ? "#1a1a1a" : "#bbb", fontSize: "0.92rem" }}>{value || placeholder}</div>
          )}
        </div>
        {!isEditing && (
          <button onClick={() => { setEditField(field); setEditVal(value || ""); }} style={{ background: "none", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#888" }}>
            <Edit2 size={13}/>
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3" }}>
      {/* Header */}
      <div style={{ background: primaryColor, padding: "48px 5vw 36px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: accentColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", fontWeight: 800, color: "#2c1810", flexShrink: 0 }}>
            {(profile.name || user.email || "U")[0].toUpperCase()}
          </div>
          <div>
            <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.4rem, 3vw, 2rem)", color: "#fff", marginBottom: 4 }}>
              {profile.name || "My Account"}
            </h1>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.88rem" }}>{user.email}</div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f0e8d0" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 5vw", display: "flex" }}>
          {[
            { label: "Total Orders", value: orders.length },
            { label: "Delivered", value: orders.filter(o => o.status === "Delivered").length },
            { label: "Total Spent", value: `₹${totalSpent.toFixed(0)}` },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, padding: "20px 0", textAlign: "center", borderRight: "1px solid #f0e8d0" }}>
              <div style={{ fontFamily: "Georgia, serif", fontSize: "1.6rem", fontWeight: 800, color: primaryColor }}>{s.value}</div>
              <div style={{ fontSize: "0.72rem", color: "#aaa", letterSpacing: 1, textTransform: "uppercase", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "36px 5vw" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "start" }}>

          {/* Profile Info */}
          <div style={{ background: "#fff", borderRadius: 20, padding: 28, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.2rem", color: "#2c1810", marginBottom: 4 }}>Profile Details</h2>
            <p style={{ fontSize: "0.78rem", color: "#aaa", marginBottom: 20 }}>Click the edit icon to update</p>
            <EditableRow icon={<User size={18}/>} label="Full Name" field="name" value={profile.name} placeholder="Add your name" />
            <EditableRow icon={<Phone size={18}/>} label="Phone" field="phone" value={profile.phone} placeholder="Add phone number" />
            <EditableRow icon={<Mail size={18}/>} label="Email" field="email" value={user.email} placeholder="Email" />
            <div style={{ marginTop: 24 }}>
              <Link to="/orders" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", color: "#555", textDecoration: "none", fontSize: "0.9rem", borderBottom: "1px solid #f5f5f5" }}>
                <Package size={18} color={primaryColor} /> My Orders
              </Link>
              <button onClick={handleSignOut} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "12px 0", background: "none", border: "none",
                color: "#d32f2f", cursor: "pointer", fontSize: "0.9rem",
                width: "100%", marginTop: 8,
              }}><LogOut size={18}/> Sign Out</button>
            </div>
          </div>

          {/* Saved Addresses */}
          <div style={{ background: "#fff", borderRadius: 20, padding: 28, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.2rem", color: "#2c1810" }}>Saved Addresses</h2>
              <button onClick={() => setAddingAddress(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: primaryColor, color: "#fff", padding: "7px 14px", border: "none", borderRadius: 8, cursor: "pointer", fontSize: "0.8rem", fontWeight: 700 }}>
                <Plus size={13}/> Add
              </button>
            </div>

            {addresses.length === 0 && !addingAddress && (
              <div style={{ textAlign: "center", padding: "32px 0", color: "#bbb" }}>
                <MapPin size={36} style={{ marginBottom: 10 }} />
                <p style={{ fontSize: "0.85rem" }}>No saved addresses yet</p>
              </div>
            )}

            {addresses.map(addr => (
              <div key={addr.id} style={{ background: "#fdf6e3", borderRadius: 12, padding: "14px 16px", marginBottom: 12, position: "relative", border: `1px solid ${accentColor}40` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ background: primaryColor, color: "#fff", padding: "2px 10px", borderRadius: 20, fontSize: "0.68rem", fontWeight: 700 }}>{addr.label || "Home"}</span>
                      <span style={{ fontWeight: 700, color: "#2c1810", fontSize: "0.9rem" }}>{addr.name}</span>
                    </div>
                    <div style={{ fontSize: "0.83rem", color: "#666", lineHeight: 1.6 }}>
                      {addr.address}<br/>
                      {addr.city}{addr.state ? `, ${addr.state}` : ""} - {addr.pincode}
                    </div>
                    {addr.phone && <div style={{ fontSize: "0.78rem", color: "#888", marginTop: 4 }}>📞 {addr.phone}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button onClick={() => openEditAddress(addr)} style={{ background: "none", border: "1.5px solid #e0d8d0", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: primaryColor }}>
                      <Edit2 size={13}/>
                    </button>
                    <button onClick={() => deleteAddress(addr.id)} style={{ background: "none", border: "1.5px solid #ffcdd2", borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#d32f2f" }}>
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {addingAddress && (
              <div style={{ background: "#f9f5f0", borderRadius: 14, padding: 20, marginTop: 12 }}>
                <div style={{ fontWeight: 700, color: "#2c1810", marginBottom: 14, fontSize: "0.9rem" }}>
                  {editAddrId ? "✏️ Edit Address" : "➕ New Address"}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[
                    { name: "label", placeholder: "Label (Home/Office)", full: true },
                    { name: "name", placeholder: "Full Name *" },
                    { name: "phone", placeholder: "Phone" },
                    { name: "address", placeholder: "Street Address *", full: true },
                    { name: "city", placeholder: "City" },
                    { name: "state", placeholder: "State" },
                    { name: "pincode", placeholder: "PIN Code *" },
                  ].map(f => (
                    <div key={f.name} style={{ gridColumn: f.full ? "1/-1" : "auto" }}>
                      <input value={addrForm[f.name] || ""} onChange={e => setAddrForm(p => ({ ...p, [f.name]: e.target.value }))}
                        placeholder={f.placeholder} style={{ ...inputStyle, padding: "9px 10px", fontSize: "0.83rem" }} />
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button onClick={() => setAddingAddress(false)} style={{ flex: 1, padding: 10, border: "1.5px solid #e0e0e0", background: "none", borderRadius: 8, cursor: "pointer", fontSize: "0.85rem" }}>Cancel</button>
                  <button onClick={saveAddress} disabled={savingAddr} style={{ flex: 2, padding: 10, background: primaryColor, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: "0.85rem" }}>
                    {savingAddr ? "Saving..." : "Save Address"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent orders preview */}
        {orders.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 20, padding: 28, marginTop: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.2rem", color: "#2c1810" }}>Recent Orders</h2>
              <Link to="/orders" style={{ color: primaryColor, textDecoration: "none", fontSize: "0.85rem", fontWeight: 700 }}>View All →</Link>
            </div>
            {orders.slice(0, 3).map(order => {
              const STATUS_COLOR = { "Pending Payment": "#f57c00", "Payment Received": "#1976d2", "Processing": "#7b1fa2", "Shipped": "#0288d1", "Delivered": "#388e3c", "Cancelled": "#d32f2f" };
              return (
                <div key={order.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f5f5f5" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontFamily: "monospace", fontSize: "0.88rem", color: "#2c1810" }}>#{order.id?.slice(0, 8).toUpperCase()}</div>
                    <div style={{ fontSize: "0.75rem", color: "#aaa" }}>{order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN") : ""}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: primaryColor }}>₹{Number(order.total || 0).toFixed(0)}</div>
                    <div style={{ fontSize: "0.72rem", color: STATUS_COLOR[order.status] || "#888", fontWeight: 700 }}>{order.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
